import { useState, useEffect } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.mjs?url';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

// Keyed by source reference (File or Blob) so repeated renders of the same
// attachment across re-renders don't re-run pdf.js.
const cache = new WeakMap();

export async function renderPdfThumbnail(source, { width = 128 } = {}) {
  if (cache.has(source)) return cache.get(source);

  const promise = (async () => {
    const buf = await source.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: buf }).promise;
    const page = await pdf.getPage(1);
    const unscaled = page.getViewport({ scale: 1 });
    const viewport = page.getViewport({ scale: width / unscaled.width });

    const canvas = document.createElement('canvas');
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;

    return canvas.toDataURL('image/png');
  })();

  cache.set(source, promise);
  return promise;
}

// Renders the first page of a PDF File/Blob to a data URL. Returns null
// while rendering or if `source` is falsy/rendering fails (icon fallback).
export function usePdfThumbnail(source, opts) {
  const [thumbUrl, setThumbUrl] = useState(null);

  useEffect(() => {
    if (!source) {
      setThumbUrl(null);
      return;
    }
    let alive = true;
    setThumbUrl(null);
    renderPdfThumbnail(source, opts)
      .then((url) => { if (alive) setThumbUrl(url); })
      .catch((err) => {
        console.error('PDF thumbnail render failed:', err);
        if (alive) setThumbUrl(null);
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  return thumbUrl;
}
